// AFFILIATE TRACKING (Afglo)
//
// Removal instructions:
// 1. Delete this file (esim-portal/src/utils/afglo.js)
// 2. Remove import+init lines from main.jsx
// 3. Remove dns-prefetch entry from index.html
// 4. Remove afglo_referral from Zod schemas, destructures, and metadata spreads in backend routes
// 5. Remove getAfgloReferral imports from PaymentConfirm.jsx, purchaseService.js, CreditPurchaseModal.jsx
// 6. Remove afglo.com from CSP in render.yaml and _headers
// Grep for "AFGLO", "afglo", or "AFFILIATE" to find all references.
//
// GTM Tags (configured in Google Tag Manager, not in code):
// - "Afglo Conversion" — Custom HTML tag that calls Afglo.convert() with revenue from dataLayer.
//   Fires on triggers: CE - PayGo Payment, CE - Subscription Payment.
//   Uses variables: {{DL - Revenue}} and {{DL - Currency}} (case-sensitive).
//   Published 04/17/2026.
// - "Afglo Lead" — Custom HTML tag that calls Afglo.lead() with userId from dataLayer.
//   Fires on trigger: CE - User Signup. Uses variable: {{DL - User ID}}.
//   Published 04/17/2026.

// ── Afglo ──

const AFGLO_API_KEY = '19391a';

// Referral is cached once ready, so subsequent reads are synchronous
let _afgloReferral = null;

/**
 * Dynamically loads the Afglo tracking script and queue.
 * When a visitor arrives via ?via=TOKEN, the script records the visit
 * and stores a 60-day cookie.
 */
export function initAfglo() {
  if (!AFGLO_API_KEY) {
    return;
  }

  try {
    // Set up the Afglo queue before loading the script
    // This allows Afglo('ready', fn) calls before the script loads
    window.Afglo = window.Afglo || function() {
      (window.Afglo.q = window.Afglo.q || []).push(arguments);
    };

    const script = document.createElement('script');
    script.src = 'https://afglo.com/rw.js';
    script.async = true;
    script.setAttribute('data-afglo', AFGLO_API_KEY);
    script.onload = () => {
      console.log('[Afglo] Script loaded');
    };
    script.onerror = () => {
      console.warn('[Afglo] Failed to load script');
    };
    document.head.appendChild(script);

    // Restore referral from localStorage (survives OAuth redirects)
    const stored = localStorage.getItem('afglo_referral');
    if (stored) {
      _afgloReferral = stored;
    }

    // Use the CustomEvent (not the queue-based Afglo('ready', fn)) to avoid
    // triggering a bug where ensureCallable() overwrites the hydrated API
    // with noop when pending queue items exist.
    window.addEventListener('Afglo.ready', function() {
      _afgloReferral = window.Afglo?.referral || null;
      if (_afgloReferral) {
        localStorage.setItem('afglo_referral', _afgloReferral);
        console.log('[Afglo] Referral captured:', _afgloReferral);
      }
    });
  } catch (e) {
    console.warn('[Afglo] Init error:', e);
  }
}

/**
 * Track a lead (signup) in Afglo.
 * If the SDK is already hydrated, calls .lead() immediately.
 * Otherwise listens for the Afglo.ready CustomEvent (avoids the
 * queue-based Afglo('ready', fn) which triggers an SDK bug).
 */
export function trackAfgloLead(email) {
  try {
    // SDK already loaded and hydrated with .lead()
    if (typeof window.Afglo?.lead === 'function') {
      window.Afglo.lead({ email });
      console.log('[Afglo] Lead tracked:', email);
      return;
    }

    // SDK not ready yet — wait for CustomEvent
    window.addEventListener('Afglo.ready', function onReady() {
      window.removeEventListener('Afglo.ready', onReady);
      if (typeof window.Afglo?.lead === 'function') {
        window.Afglo.lead({ email });
        console.log('[Afglo] Lead tracked (deferred):', email);
      }
    });
  } catch (e) {
    console.warn('[Afglo] Lead tracking error:', e);
  }
}

/**
 * Returns the current Afglo referral ID, or null if not available.
 * Uses cached value from ready callback for reliability.
 */
export function getAfgloReferral() {
  try {
    return _afgloReferral || window.Afglo?.referral || localStorage.getItem('afglo_referral') || null;
  } catch {
    return null;
  }
}
